import { loadToken } from '../profile/accountStore';

export interface AccountDTO {
  id: string;
  name: string;
  online_wins: number;
  xp: number;
  level: number;
  xp_into_level: number;
  xp_to_next: number;
  max_level: number;
}

export interface LeaderboardEntry {
  name: string;
  online_wins: number;
  xp: number;
  level: number;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

const BASE: string = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() || '/api';

async function call<T>(path: string, init: RequestInit = {}, opts: { auth?: boolean } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (opts.auth) {
    const token = loadToken();
    if (!token) throw new ApiError(401, 'no_token', 'no local token');
    headers.set('Authorization', `Bearer ${token}`);
  }
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'omit' });
  } catch (e) {
    throw new ApiError(0, 'network', (e as Error)?.message ?? 'network error');
  }
  let json: any = null;
  try {
    json = await res.json();
  } catch {}
  if (!res.ok) {
    const code = json?.error?.code ?? `http_${res.status}`;
    const msg = json?.error?.message ?? code;
    throw new ApiError(res.status, code, msg);
  }
  return json as T;
}

export const accountApi = {
  async checkName(name: string): Promise<{ available: boolean; reason?: string }> {
    return call('/account/check-name', { method: 'POST', body: JSON.stringify({ name }) });
  },
  async register(name: string): Promise<{ token: string; account: AccountDTO }> {
    return call('/account/register', { method: 'POST', body: JSON.stringify({ name }) });
  },
  async me(): Promise<{ account: AccountDTO }> {
    return call('/account/me', { method: 'GET' }, { auth: true });
  },
  async rename(name: string): Promise<{ account: AccountDTO }> {
    return call('/account/rename', { method: 'POST', body: JSON.stringify({ name }) }, { auth: true });
  },
  async rotateToken(): Promise<{ token: string; account: AccountDTO }> {
    return call('/account/rotate-token', { method: 'POST' }, { auth: true });
  },
  async importToken(token: string): Promise<{ account: AccountDTO }> {
    return call('/account/import', { method: 'POST', body: JSON.stringify({ token }) });
  },
  async leaderboard(limit = 50): Promise<{ entries: LeaderboardEntry[] }> {
    return call(`/leaderboard?limit=${limit}`, { method: 'GET' });
  },
};
